<template>
  <div v-if="!isSSRESelection" id="refund-details" class="padding-top-s large-padding-top-m small-border-top">
    <div class="grid-x align-justify">
      Subtotal
      <span id="subtotal">{{ formatPrice(estimatedRefundStore.estimatedRefund.merchandiseTotal) ?? '$0.00' }}</span>
    </div>
    <div v-if="showProtectionPlanFee" class="grid-x align-justify margin-top-xxs">
      Allstate Protection Plan
      <span id="protection-plan-fee">{{ formatPrice(estimatedRefundStore.estimatedRefund.protectionPlanTotal) }}</span>
    </div>
    <div v-if="showEwasteFee" class="grid-x align-justify margin-top-xxs">
      eWaste Recycling Fee
      <span id="ewaste-fee">{{ formatPrice(estimatedRefundStore.estimatedRefund.eWasteFee) }}</span>
    </div>
    <div v-if="estimatedRefundStore.estimatedRefund.batteryFee" class="grid-x align-justify margin-top-xxs">
      <div class="grid-x align-middle">
        CA embedded battery fee
        <button
          v-tooltip.bottom="
            `This fee is required by California law for products with batteries that cannot easily be removed.
            It funds programs for safe recycling and disposal.
            The fee is calculated based on the product price, with a cap per item.`
          "
          class="tooltip-small"
          aria-label="More Information"
        ></button>
      </div>
      <span id="battery-fee">{{ formatPrice(estimatedRefundStore.estimatedRefund.batteryFee) }}</span>
    </div>
    <div
      v-if="estimatedRefundStore.estimatedRefund?.totalTaxes && Number(estimatedRefundStore.estimatedRefund.totalTaxes) !== 0"
      class="grid-x align-justify margin-top-xxs"
    >
      Estimated Tax
      <span id="tax">{{ formatPrice(estimatedRefundStore.estimatedRefund.totalTaxes) }}</span>
    </div>
    <div
      v-if="returnItemsStore.SHOW_CONDITIONAL_RETURN_FEE && estimatedRefundStore.estimatedRefund?.shippingFee"
      class="grid-x align-justify margin-top-xxs"
    >
      Estimated Return Shipping Fee
      <span id="shipping-fee">{{
        estimatedRefundStore.estimatedRefund.shippingFee === 0.0 ? FREE_SHIPMENT : `-${formatPrice(estimatedRefundStore.estimatedRefund.shippingFee)}`
      }}</span>
    </div>
  </div>
  <div
    class="grid-x align-justify"
    :class="
      isSSRESelection
        ? 'padding-top-s large-padding-top-m small-border-top'
        : 'margin-top-s large-margin-top-m padding-top-s large-padding-top-m border-top'
    "
  >
    <h4 v-text="isSSRESelection ? config.refundDetails.estimatedAmountDue : config.refundDetails.estimatedRefundTotal"></h4>
    <h4 id="estimated-refund" v-text="estimateRefundAmount"></h4>
  </div>
  <div v-if="!returnItemsStore.SUBMIT_DISABLED" aria-live="polite" aria-atomic="true" class="visibility-hidden">
    {{ screenReaderContent }}
  </div>
</template>
<!-- eslint-disable-next-line vue/component-tags-order -->
<script>
import Tooltip from '@nuxt-common/atomic-ui/app/components/tooltip/tooltip';

export default {
  directives: {
    tooltip: Tooltip,
  },
};
</script>
<script setup>
/* eslint-disable import/first */
import { computed, watch, ref } from 'vue';
import { formatPrice } from '@/utils/returns/General';
import { FREE_SHIPMENT } from '#shared/constants';

// Config
import * as config from 'config';

//Pinia Store
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';
import { useEstimatedRefundStore } from '@/stores/returns/useEstimatedRefundStore';
const returnItemsStore = useReturnItemStore();
const estimatedRefundStore = useEstimatedRefundStore();

const screenReaderContent = ref('');
const showEwasteFee = computed(
  () =>
    config.brand === 'mcom' &&
    returnItemsStore.IS_MARKETPLACE_ORDER &&
    estimatedRefundStore.estimatedRefund.eWasteFee &&
    estimatedRefundStore.estimatedRefund.eWasteFee !== 0.0,
);
const showProtectionPlanFee = computed(
  () => estimatedRefundStore.estimatedRefund.protectionPlanTotal && estimatedRefundStore.estimatedRefund.protectionPlanTotal !== 0.0,
);
const isSSRESelection = computed(() =>
  [config.REFUND_METHOD.EXCHANGE, config.REFUND_METHOD.REPLACEMENT].includes(returnItemsStore.returnSelections.refundOption?.choice),
);

const estimateRefundAmount = computed(() => {
  if (isSSRESelection.value) return formatPrice(estimatedRefundStore.estimatedRefund.amountDue || 0);
  return formatPrice(estimatedRefundStore.estimatedRefund.totalRefundAmount || 0);
});

const announceChange = (message) => {
  screenReaderContent.value = message;
};

const composeMessage = (rDetail) => {
  let message = `${config.ORDER_DETAILS.paymentInformation.subtotalText}: ${formatPrice(rDetail.merchandiseTotal)}, `;

  if (showEwasteFee.value) {
    message += `${config.ORDER_DETAILS.paymentInformation.eWasteRecyclingFeeText}: ${formatPrice(rDetail.eWasteFee)}, `;
  }

  if (rDetail.batteryFee) {
    message += `${config.ORDER_DETAILS.paymentInformation.batteryFeeText}: ${formatPrice(rDetail.batteryFee)}, `;
  }

  message += `${config.ORDER_DETAILS.paymentInformation.estimatedTax}: ${formatPrice(rDetail.totalTaxes)}, `;
  if (returnItemsStore.SHOW_CONDITIONAL_RETURN_FEE && rDetail?.shippingFee) {
    message += `${config.ORDER_DETAILS.paymentInformation.estimatedReturnShippingFee}: -${formatPrice(rDetail.shippingFee)}, `;
  }
  message += `${config.refundDetails.estimatedRefundTotal}: ${formatPrice(rDetail?.totalRefundAmount) ?? '$0.00'}`;
  return message;
};

watch(estimatedRefundStore, (newVal) => {
  if (!newVal || !newVal.estimatedRefund || !Object.keys(newVal.estimatedRefund).length) {
    return;
  }

  if (isSSRESelection.value) {
    announceChange(config.refundDetails.estimatedAmountDue + estimateRefundAmount.value);
    return;
  }
  announceChange(composeMessage(newVal.estimatedRefund));
});
</script>

<style lang="scss" scoped brand="mcom">
@import '~@core/csg/src/scss/encodedIcons/mcom/_tooltip-small.scss';
</style>
<style lang="scss" scoped brand="bcom">
@import '~@core/csg/src/scss/encodedIcons/bcom/_tooltip-small.scss';
</style>
